/**
 * EquipeCongesTab — Admin-facing leave view inside the Équipe tab.
 *
 * Two panes:
 *   1. Pending requests (top) — each with approve / deny inline.
 *   2. Quota config table — legal_days + base_hours per active user.
 *   Optional: full history filterable by user/status (kept as a third pane below).
 */

import React, { useMemo, useState } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import {
  useAllLeaveRequests,
  useDecideLeaveRequest,
  useAllLeaveConfigs,
  useUpsertLeaveConfig,
  useHolidays,
} from '@/features/admin/tabs/mon-espace/hooks/useLeave';
import type { LeaveRequestEnriched, LeaveType, UserLeaveConfigEntry } from '@/features/admin/tabs/mon-espace/api/leave.api';
import { LeaveCalendar, CalendarEntry } from '@/features/admin/tabs/mon-espace/components/LeaveCalendar/LeaveCalendar';
import { expandLeaveRequest, workingDowSet } from '@/features/admin/tabs/mon-espace/components/LeaveCalendar/expandRequest';
import { DayDetailDrawer } from './DayDetailDrawer';
import './EquipeCongesTab.css';

const TYPE_LABEL: Record<LeaveType, string> = {
  legal: 'Congé légal',
  recup: 'Récupération',
  unpaid: 'Sans solde',
  sick: 'Maladie',
};

const fmtDate = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const userName = (r: { first_name?: string; last_name?: string; user_email?: string }) =>
  `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.user_email || '?';

// --- Pending request row ---------------------------------------------------

const PendingRequestRow: React.FC<{ req: LeaveRequestEnriched }> = ({ req }) => {
  const decide = useDecideLeaveRequest();
  const [showDeny, setShowDeny] = useState(false);
  const [denyReason, setDenyReason] = useState('');

  const dateRange = useMemo(() => {
    if (req.date_from === req.date_to) {
      const suffix = req.half_day_start === 'full' ? '' : (req.half_day_start === 'am' ? ' (matin)' : ' (après-midi)');
      return `${fmtDate(req.date_from)}${suffix}`;
    }
    return `${fmtDate(req.date_from)} → ${fmtDate(req.date_to)}`;
  }, [req]);

  const approve = () => {
    decide.mutate({ id: req.id, status: 'approved' }, {
      onSuccess: () => toast.success(`Demande de ${userName(req)} approuvée`),
      onError: () => toast.error('Erreur'),
    });
  };

  const submitDeny = () => {
    decide.mutate({ id: req.id, status: 'denied', reason: denyReason.trim() || undefined }, {
      onSuccess: () => {
        toast.success('Demande refusée');
        setShowDeny(false);
        setDenyReason('');
      },
      onError: () => toast.error('Erreur'),
    });
  };

  return (
    <div className="eq-conge-pending-row">
      <div className="eq-conge-pending-main">
        <div className="eq-conge-pending-user">{userName(req)}</div>
        <div className="eq-conge-pending-meta">
          <span className="eq-conge-pending-dates">{dateRange}</span>
          <span className="eq-conge-pending-type">{TYPE_LABEL[req.type]}</span>
          {req.reason && <span className="eq-conge-pending-reason">— {req.reason}</span>}
        </div>
      </div>
      {!showDeny ? (
        <div className="eq-conge-pending-actions">
          <button className="eq-conge-btn-approve" onClick={approve} disabled={decide.isPending}>
            <CheckCircleIcon className="w-4 h-4" /> Approuver
          </button>
          <button className="eq-conge-btn-deny" onClick={() => setShowDeny(true)} disabled={decide.isPending}>
            <XCircleIcon className="w-4 h-4" /> Refuser
          </button>
        </div>
      ) : (
        <div className="eq-conge-deny-form">
          <input
            className="eq-conge-deny-input"
            placeholder="Motif du refus (optionnel)"
            value={denyReason}
            onChange={e => setDenyReason(e.target.value)}
            autoFocus
            onKeyDown={e => {
              if (e.key === 'Enter') submitDeny();
              else if (e.key === 'Escape') { setShowDeny(false); setDenyReason(''); }
            }}
          />
          <button className="eq-conge-btn-deny" onClick={submitDeny} disabled={decide.isPending}>
            Confirmer refus
          </button>
          <button className="eq-conge-btn-cancel" onClick={() => { setShowDeny(false); setDenyReason(''); }}>
            Annuler
          </button>
        </div>
      )}
    </div>
  );
};

// --- Config row ------------------------------------------------------------

// Default working-days picker — Mon→Sun in canonical order, short FR labels.
const WORKING_DAY_DEFS: { key: string; short: string }[] = [
  { key: 'monday', short: 'L' },
  { key: 'tuesday', short: 'Ma' },
  { key: 'wednesday', short: 'Me' },
  { key: 'thursday', short: 'J' },
  { key: 'friday', short: 'V' },
  { key: 'saturday', short: 'S' },
  { key: 'sunday', short: 'D' },
];
const DEFAULT_WORKING_DAYS_CSV = 'monday,tuesday,wednesday,thursday,friday';
const parseWorkingDays = (csv?: string): Set<string> =>
  new Set((csv || DEFAULT_WORKING_DAYS_CSV).split(',').map(s => s.trim()).filter(Boolean));
const serializeWorkingDays = (days: Set<string>): string =>
  WORKING_DAY_DEFS.filter(d => days.has(d.key)).map(d => d.key).join(',');

const ConfigRow: React.FC<{ row: UserLeaveConfigEntry; year: number }> = ({ row, year }) => {
  const [editing, setEditing] = useState(false);
  const [legalDays, setLegalDays] = useState(String(row.legal_leave_days));
  const [baseHours, setBaseHours] = useState(String(row.base_hours_per_week));
  const [trackingStart, setTrackingStart] = useState(row.recup_tracking_start || '');
  const [employmentType, setEmploymentType] = useState<'employee' | 'independent'>(row.employment_type || 'independent');
  const [workingDays, setWorkingDays] = useState<Set<string>>(parseWorkingDays(row.working_days));
  const upsert = useUpsertLeaveConfig();

  const toggleDay = (key: string) => setWorkingDays(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const save = () => {
    const ld = parseFloat(legalDays);
    const bh = parseFloat(baseHours);
    if (isNaN(ld) || ld < 0 || isNaN(bh) || bh < 0) {
      toast.error('Valeurs invalides');
      return;
    }
    if (trackingStart && !/^\d{4}-\d{2}-\d{2}$/.test(trackingStart)) {
      toast.error('Date de démarrage invalide');
      return;
    }
    const wdCsv = serializeWorkingDays(workingDays);
    if (!wdCsv) {
      toast.error('Sélectionne au moins un jour travaillé');
      return;
    }
    upsert.mutate(
      {
        userId: row.user_id, year,
        legalLeaveDays: ld, baseHoursPerWeek: bh,
        recupTrackingStart: trackingStart || undefined,
        employmentType,
        workingDays: wdCsv,
      },
      {
        onSuccess: () => { toast.success('Configuration sauvegardée'); setEditing(false); },
        onError: () => toast.error('Erreur'),
      }
    );
  };

  return (
    <tr>
      <td className="eq-conge-cfg-user">
        <span className="eq-conge-cfg-name">{userName({ first_name: row.first_name, last_name: row.last_name, user_email: row.email })}</span>
        <span className="eq-conge-cfg-role">{row.role}</span>
      </td>
      <td>
        {editing ? (
          <select
            className="eq-conge-cfg-input"
            style={{ width: 130 }}
            value={employmentType}
            onChange={e => setEmploymentType(e.target.value as 'employee' | 'independent')}
          >
            <option value="employee">Employé</option>
            <option value="independent">Indépendant</option>
          </select>
        ) : (
          // One-click toggle — independant ↔ employé without entering full edit mode.
          // Confirms first because flipping changes which compteurs apply to the user.
          <button
            type="button"
            className={`eq-conge-type-toggle ${row.employment_type === 'employee' ? 'is-employee' : 'is-independent'}`}
            title={`Basculer vers ${row.employment_type === 'employee' ? 'Indépendant' : 'Employé'}`}
            disabled={upsert.isPending}
            onClick={() => {
              const next = row.employment_type === 'employee' ? 'independent' : 'employee';
              const label = next === 'employee' ? 'Employé' : 'Indépendant';
              if (!confirm(`Basculer ${row.first_name} ${row.last_name} vers "${label}" ?`)) return;
              upsert.mutate(
                // Only flip the type — legal/base/tracking/working-days are
                // preserved server-side (COALESCE), so we don't risk
                // overwriting them with possibly-stale local edit-state.
                { userId: row.user_id, year, employmentType: next },
                {
                  onSuccess: () => { setEmploymentType(next); toast.success(`${row.first_name} ${row.last_name} → ${label}`); },
                  onError: () => toast.error('Erreur'),
                }
              );
            }}
          >
            {row.employment_type === 'employee' ? 'Employé' : 'Indépendant'}
          </button>
        )}
      </td>
      <td className="eq-conge-cfg-num">
        {editing ? (
          <input
            type="number"
            step="0.5"
            min="0"
            className="eq-conge-cfg-input"
            value={legalDays}
            onChange={e => setLegalDays(e.target.value)}
          />
        ) : (
          <>{row.legal_leave_days} j</>
        )}
      </td>
      <td className="eq-conge-cfg-num">
        {editing ? (
          <input
            type="number"
            step="0.5"
            min="0"
            className="eq-conge-cfg-input"
            value={baseHours}
            onChange={e => setBaseHours(e.target.value)}
          />
        ) : (
          <>{row.base_hours_per_week} h/sem</>
        )}
      </td>
      <td>
        {editing ? (
          <div className="eq-conge-workdays">
            {WORKING_DAY_DEFS.map(d => (
              <button
                key={d.key}
                type="button"
                className={`eq-conge-workday ${workingDays.has(d.key) ? 'is-on' : ''}`}
                title={d.key}
                onClick={() => toggleDay(d.key)}
              >
                {d.short}
              </button>
            ))}
          </div>
        ) : (
          <span className="eq-conge-workdays-display">
            {WORKING_DAY_DEFS.filter(d => parseWorkingDays(row.working_days).has(d.key)).map(d => d.short).join(' ') || '—'}
          </span>
        )}
      </td>
      <td>
        {editing ? (
          <input
            type="date"
            className="eq-conge-cfg-input"
            style={{ width: 140 }}
            value={trackingStart}
            onChange={e => setTrackingStart(e.target.value)}
          />
        ) : (
          row.recup_tracking_start
            ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{row.recup_tracking_start}</span>
            : <span className="eq-conge-cfg-flag eq-conge-cfg-flag--default">défaut</span>
        )}
      </td>
      <td>
        {row.has_config
          ? <span className="eq-conge-cfg-flag">configuré</span>
          : <span className="eq-conge-cfg-flag eq-conge-cfg-flag--default">défaut</span>}
      </td>
      <td className="eq-conge-cfg-actions">
        {editing ? (
          <>
            <button className="eq-conge-btn-save" onClick={save} disabled={upsert.isPending}>Enregistrer</button>
            <button className="eq-conge-btn-cancel" onClick={() => {
              setEditing(false);
              setLegalDays(String(row.legal_leave_days));
              setBaseHours(String(row.base_hours_per_week));
              setTrackingStart(row.recup_tracking_start || '');
              setEmploymentType(row.employment_type || 'independent');
              setWorkingDays(parseWorkingDays(row.working_days));
            }}>Annuler</button>
          </>
        ) : (
          <button className="eq-conge-icon-btn" title="Modifier" onClick={() => setEditing(true)}>
            <PencilIcon className="w-4 h-4" />
          </button>
        )}
      </td>
    </tr>
  );
};

// --- Main tab --------------------------------------------------------------

const ymd = (d: Date) => d.toISOString().slice(0, 10);

export const EquipeCongesTab: React.FC<{ canManage?: boolean }> = ({ canManage = true }) => {
  const now = useMemo(() => new Date(), []);
  const currentYear = now.getFullYear();
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(now.getMonth());

  const { data: pending = [], isLoading: pendingLoading } = useAllLeaveRequests({ status: 'pending', year });
  const { data: configs = [], isLoading: configsLoading } = useAllLeaveConfigs(year);
  const { data: allYear = [] } = useAllLeaveRequests({ year });

  // History excludes pending (already shown above), shows most recent first
  const history = useMemo(
    () => allYear.filter(r => r.status !== 'pending').slice(0, 50),
    [allYear]
  );

  // Calendar data: all approved + pending leaves of the year (already fetched)
  // plus public holidays in the visible month window.
  const monthRange = useMemo(() => {
    const start = new Date(Date.UTC(year, month, 1));
    start.setUTCDate(start.getUTCDate() - 14);
    const end = new Date(Date.UTC(year, month + 1, 0));
    end.setUTCDate(end.getUTCDate() + 14);
    return { from: ymd(start), to: ymd(end) };
  }, [year, month]);
  const { data: holidays = [] } = useHolidays(monthRange);

  const teamEntriesByDate = useMemo(() => {
    const m = new Map<string, CalendarEntry[]>();
    const holidaySet = new Set(holidays.map(h => h.date));
    // Per-user working days so part-time members' off-days don't show chips.
    const workingByUser = new Map(configs.map(c => [c.user_id, workingDowSet(c.working_days)]));
    for (const h of holidays) {
      const arr = m.get(h.date) || [];
      arr.push({ kind: 'holiday', label: h.label, tooltip: `Férié: ${h.label}` });
      m.set(h.date, arr);
    }
    for (const req of allYear) {
      if (req.status === 'cancelled' || req.status === 'denied') continue;
      const days = expandLeaveRequest(req, { holidays: holidaySet, workingDays: workingByUser.get(req.user_id) });
      const label = `${(req.first_name || '').charAt(0)}. ${req.last_name || ''}`.trim() || req.user_email || '?';
      for (const d of days) {
        const arr = m.get(d.date) || [];
        arr.push({
          kind: 'leave',
          label,
          type: req.type,
          status: req.status,
          fraction: d.fraction,
          userId: req.user_id,
          tooltip: `${req.first_name || ''} ${req.last_name || ''} · ${TYPE_LABEL[req.type]}${req.status === 'pending' ? ' (en attente)' : ''}${d.fraction < 1 ? ' · demi-journée' : ''}`,
        });
        m.set(d.date, arr);
      }
    }
    return m;
  }, [allYear, holidays, configs]);

  const goPrevMonth = () => { if (month === 0) { setMonth(11); setYear(year - 1); } else setMonth(month - 1); };
  const goNextMonth = () => { if (month === 11) { setMonth(0); setYear(year + 1); } else setMonth(month + 1); };
  const goToday = () => { setYear(currentYear); setMonth(now.getMonth()); };

  // Day-detail drawer state — admin clicks a cell to inspect who's off/avail
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  return (
    <div className="eq-conge-tab">
      {/* Year selector */}
      <div className="eq-conge-toolbar">
        <label className="eq-conge-year-label">Année</label>
        <select className="eq-conge-year-select" value={year} onChange={e => {
          const y = parseInt(e.target.value, 10);
          setYear(y);
          if (y === currentYear) setMonth(now.getMonth());
          else setMonth(0);
        }}>
          {[currentYear - 1, currentYear, currentYear + 1].map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {/* Team calendar */}
      <section className="eq-conge-section">
        <h3 className="eq-conge-section-title">Calendrier équipe</h3>
        <p className="eq-conge-section-hint">
          Aperçu mensuel des congés (approuvés + en attente, contour pointillé) et jours fériés. Chaque collaborateur a sa couleur.
        </p>
        <LeaveCalendar
          year={year}
          month={month}
          onPrevMonth={goPrevMonth}
          onNextMonth={goNextMonth}
          onToday={goToday}
          entriesByDate={teamEntriesByDate}
          onDayClick={setSelectedDate}
        />
      </section>

      {/* Pending — approvals are admin-only */}
      {canManage && (
      <section className="eq-conge-section">
        <h3 className="eq-conge-section-title">
          <ExclamationCircleIcon className="w-5 h-5" />
          Demandes en attente
          {pending.length > 0 && <span className="eq-conge-count-badge">{pending.length}</span>}
        </h3>
        {pendingLoading ? (
          <div className="eq-conge-loading">Chargement…</div>
        ) : pending.length === 0 ? (
          <div className="eq-conge-empty">Aucune demande en attente.</div>
        ) : (
          <div className="eq-conge-pending-list">
            {pending.map(req => <PendingRequestRow key={req.id} req={req} />)}
          </div>
        )}
      </section>
      )}

      {/* Config — quota/base editing is admin-only */}
      {canManage && (
      <section className="eq-conge-section">
        <h3 className="eq-conge-section-title">Quota et base horaire {year}</h3>
        <p className="eq-conge-section-hint">
          Définir les congés légaux annuels et la base hebdomadaire pour le compteur récup.
        </p>
        {configsLoading ? (
          <div className="eq-conge-loading">Chargement…</div>
        ) : (
          <div className="eq-conge-cfg-table-wrapper">
            <table className="eq-conge-cfg-table">
              <thead>
                <tr>
                  <th>Utilisateur</th>
                  <th>Type</th>
                  <th>Congés légaux</th>
                  <th>Base hebdo</th>
                  <th>Jours travaillés</th>
                  <th>Récup depuis</th>
                  <th>État</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {configs.map(row => <ConfigRow key={row.user_id} row={row} year={year} />)}
              </tbody>
            </table>
          </div>
        )}
      </section>
      )}

      {/* History */}
      <section className="eq-conge-section">
        <h3 className="eq-conge-section-title">Historique récent</h3>
        {history.length === 0 ? (
          <div className="eq-conge-empty">Aucune décision dans l'année.</div>
        ) : (
          <div className="eq-conge-history-list">
            {history.map(req => (
              <div key={req.id} className={`eq-conge-history-row eq-conge-history-row--${req.status}`}>
                <span className="eq-conge-history-user">{userName(req)}</span>
                <span className="eq-conge-history-dates">
                  {req.date_from === req.date_to ? fmtDate(req.date_from) : `${fmtDate(req.date_from)} → ${fmtDate(req.date_to)}`}
                </span>
                <span className="eq-conge-history-type">{TYPE_LABEL[req.type]}</span>
                <span className={`eq-conge-history-status eq-conge-history-status--${req.status}`}>
                  {req.status === 'approved' ? 'Approuvé' : req.status === 'denied' ? 'Refusé' : 'Annulé'}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {selectedDate && (
        <DayDetailDrawer
          dateIso={selectedDate}
          allLeaves={allYear}
          configs={configs}
          holidays={holidays}
          onClose={() => setSelectedDate(null)}
        />
      )}
    </div>
  );
};

export default EquipeCongesTab;
